import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconLaptop } from '../components/layout/NavIcons';
import { formatRelativeTime } from '../lib/timeFormat';
import {
  getNetworkAssets,
  getNetworkEvents,
  resolveNetworkEvent,
  type NetworkAssetRow,
  type NetworkEventRow,
} from '../server/api';

function displayDataSource(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '—';
  if (/osquery/i.test(s)) return 'ShieldSME agent';
  return s;
}

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low';

const EVENT_BORDER: Record<string, string> = {
  critical: '#DC2626',
  high: '#F59E0B',
  medium: '#3B82F6',
  low: '#6B7280',
};

function msSince(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return Date.now() - t;
}

function riskBadge(score: number): { label: string; style: React.CSSProperties } {
  if (score <= 29) return { label: 'Low', style: { color: 'var(--accent-green)', backgroundColor: 'color-mix(in srgb, var(--accent-green) 12%, transparent)' } };
  if (score <= 59)
    return { label: 'Medium', style: { color: 'var(--accent-amber)', backgroundColor: 'color-mix(in srgb, var(--accent-amber) 12%, transparent)' } };
  if (score <= 79)
    return { label: 'High', style: { color: 'var(--accent-danger)', backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)' } };
  return { label: 'Critical', style: { color: 'var(--accent-danger)', backgroundColor: 'color-mix(in srgb, var(--accent-danger) 18%, transparent)' } };
}

function statusBadge(status: string | null | undefined): { label: string; className: string } {
  const s = (status ?? '').toLowerCase();
  if (s === 'offline') return { label: 'Offline', className: 'bg-border text-muted' };
  if (s === 'inactive') return { label: 'Inactive', className: 'bg-warning/15 text-warning' };
  return { label: 'Active', className: 'bg-accent/15 text-accent' };
}

function SummarySkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border border-border bg-surface/80 p-4"
          style={{ minHeight: 100 }}
        >
          <div className="mb-3 h-4 w-24 rounded bg-border" />
          <div className="h-8 w-16 rounded bg-border" />
        </div>
      ))}
    </div>
  );
}

function SectionSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-lg border border-border bg-surface/60 p-4">
          <div className="mb-2 h-4 w-3/4 max-w-md rounded bg-border" />
          <div className="h-3 w-full max-w-lg rounded bg-border" />
        </div>
      ))}
    </div>
  );
}

function IconMonitor({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function IconWarning({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function IconActivity({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function IconShield({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3L4 6v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V6l-8-3z"
      />
    </svg>
  );
}

function IconShieldCheck({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3L4 6v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V6l-8-3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconChevron({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function NetworkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const assetFilter = searchParams.get('asset');

  const [assets, setAssets] = useState<NetworkAssetRow[]>([]);
  const [events, setEvents] = useState<NetworkEventRow[]>([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsHasMore, setEventsHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(() => Date.now());
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);

  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [visibleEventCount, setVisibleEventCount] = useState(20);

  const [toast, setToast] = useState<{ message: string; variant: 'error' } | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const assetNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of assets) {
      m.set(a.id, (a.asset_name ?? a.device_id ?? 'Device').trim() || 'Device');
    }
    return m;
  }, [assets]);

  const loadInitial = useCallback(async () => {
    setFetchError(null);
    const [aRes, eRes] = await Promise.all([
      getNetworkAssets(),
      getNetworkEvents({ limit: 50, resolved: false, page: 1 }),
    ]);
    if (!aRes.ok) setFetchError(aRes.error);
    else setAssets(aRes.assets);
    if (!eRes.ok) setFetchError((prev) => prev ?? eRes.error);
    else {
      setEvents(eRes.events);
      setEventsPage(eRes.page);
      setEventsHasMore(eRes.events.length >= 50);
    }
    setLastUpdatedAt(Date.now());
    setLoading(false);
  }, []);

  const refreshData = useCallback(async () => {
    setFetchError(null);
    const [aRes, eRes] = await Promise.all([
      getNetworkAssets(),
      getNetworkEvents({ limit: 50, resolved: false, page: 1 }),
    ]);
    if (!aRes.ok) setFetchError(aRes.error);
    else setAssets(aRes.assets);
    if (!eRes.ok) setFetchError((prev) => prev ?? eRes.error);
    else {
      setEvents(eRes.events);
      setEventsPage(1);
      setEventsHasMore(eRes.events.length >= 50);
    }
    setLastUpdatedAt(Date.now());
  }, []);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void refreshData();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [refreshData]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSecondsSinceUpdate(Math.floor((Date.now() - lastUpdatedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [lastUpdatedAt]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const filteredEvents = useMemo(() => {
    let list = events;
    if (assetFilter) list = list.filter((e) => e.asset_id === assetFilter);
    if (severityFilter !== 'all') list = list.filter((e) => e.severity.toLowerCase() === severityFilter);
    return list;
  }, [events, assetFilter, severityFilter]);

  const displayedEvents = useMemo(
    () => filteredEvents.slice(0, visibleEventCount),
    [filteredEvents, visibleEventCount]
  );

  useEffect(() => {
    setVisibleEventCount(20);
  }, [assetFilter, severityFilter]);

  const summary = useMemo(() => {
    const deviceCount = assets.length;
    const activeThreats = events.filter((e) => {
      const s = e.severity.toLowerCase();
      return s === 'high' || s === 'critical';
    }).length;
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const eventsToday = events.filter((e) => new Date(e.created_at).getTime() >= dayAgo).length;

    const criticalCount = events.filter((e) => e.severity.toLowerCase() === 'critical').length;
    const highCount = events.filter((e) => e.severity.toLowerCase() === 'high').length;
    const mediumCount = events.filter((e) => e.severity.toLowerCase() === 'medium').length;

    let healthLabel: string;
    let healthColor: string;
    if (deviceCount === 0) {
      healthLabel = 'No devices';
      healthColor = 'var(--text-secondary)';
    } else if (activeThreats > 0) {
      const score = Math.max(0, Math.min(100, 100 - criticalCount * 20 - highCount * 10 - mediumCount * 5));
      healthLabel = `${score}/100`;
      if (score >= 70) healthColor = 'var(--accent-green)';
      else if (score >= 40) healthColor = '#F59E0B';
      else healthColor = 'var(--accent-danger)';
    } else {
      const score = Math.max(0, Math.min(100, 100 - mediumCount * 5));
      healthLabel = `${score}/100`;
      if (score >= 70) healthColor = 'var(--accent-green)';
      else if (score >= 40) healthColor = '#F59E0B';
      else healthColor = 'var(--accent-danger)';
    }

    return {
      deviceCount,
      activeThreats,
      eventsToday,
      healthLabel,
      healthColor,
    };
  }, [assets, events]);

  const loadMoreEvents = async () => {
    if (visibleEventCount < filteredEvents.length) {
      setVisibleEventCount((c) => c + 20);
      return;
    }
    if (!eventsHasMore) return;
    const nextPage = eventsPage + 1;
    const res = await getNetworkEvents({ limit: 50, resolved: false, page: nextPage });
    if (!res.ok) {
      setToast({ message: res.error, variant: 'error' });
      return;
    }
    if (res.events.length === 0) {
      setEventsHasMore(false);
      return;
    }
    setEvents((prev) => {
      const seen = new Set(prev.map((e) => e.id));
      const merged = [...prev];
      for (const e of res.events) {
        if (!seen.has(e.id)) merged.push(e);
      }
      return merged;
    });
    setEventsPage(nextPage);
    setEventsHasMore(res.events.length >= 50);
    setVisibleEventCount((c) => c + 20);
  };

  const showLoadMore =
    visibleEventCount < filteredEvents.length ||
    (visibleEventCount >= filteredEvents.length && eventsHasMore && filteredEvents.length > 0);

  return (
    <div className="mx-auto max-w-6xl space-y-10">
      {toast ? (
        <div
          className="fixed bottom-6 left-1/2 z-[260] max-w-md -translate-x-1/2 rounded-lg border border-danger/40 bg-surface px-4 py-3 text-sm text-danger shadow-lg"
          role="alert"
        >
          {toast.message}
        </div>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Network Monitor</h1>
          <p className="mt-1 text-sm text-muted sm:text-base">
            Real-time visibility across your devices and connections
          </p>
        </div>
        <p className="shrink-0 text-xs text-muted sm:text-sm" aria-live="polite">
          Last updated: {secondsSinceUpdate}s ago
        </p>
      </div>

      {fetchError ? (
        <div className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
          {fetchError}
        </div>
      ) : null}

      {loading ? (
        <SummarySkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-border bg-surface/90 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-muted">Monitored Devices</p>
              <span className="inline-flex shrink-0 text-[var(--accent-green)]" aria-hidden>
                <IconMonitor className="h-8 w-8" />
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold tabular-nums" style={{ color: 'var(--accent-green)' }}>
              {summary.deviceCount}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-surface/90 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-muted">Active Threats</p>
              <span
                className="inline-flex shrink-0"
                style={{ color: summary.activeThreats > 0 ? 'var(--accent-danger)' : 'var(--text-secondary)' }}
                aria-hidden
              >
                <IconWarning className="h-8 w-8" />
              </span>
            </div>
            <p
              className="mt-2 text-3xl font-bold tabular-nums"
              style={{ color: summary.activeThreats > 0 ? 'var(--accent-danger)' : 'var(--text-secondary)' }}
            >
              {summary.activeThreats}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-surface/90 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-muted">Events Today</p>
              <span className="inline-flex shrink-0 text-[var(--accent-green)]" aria-hidden>
                <IconActivity className="h-8 w-8" />
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold tabular-nums" style={{ color: 'var(--accent-green)' }}>
              {summary.eventsToday}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-surface/90 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-muted">Network Health</p>
              <span className="inline-flex shrink-0" style={{ color: summary.healthColor }} aria-hidden>
                <IconShield className="h-8 w-8" />
              </span>
            </div>
            <p className="mt-2 text-3xl font-bold tabular-nums" style={{ color: summary.healthColor }}>
              {summary.healthLabel}
            </p>
          </div>
        </div>
      )}

      <section>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-foreground">Monitored Devices</h2>
          <Link
            to="/download"
            className="rounded-lg border-2 px-4 py-2 text-sm font-semibold transition hover:opacity-90"
            style={{ borderColor: 'var(--accent-green)', color: 'var(--accent-green)' }}
          >
            Download Agent
          </Link>
        </div>

        {loading ? (
          <SectionSkeleton rows={3} />
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-border bg-surface/80 px-6 py-12 text-center">
            <IconLaptop className="h-12 w-12 text-muted" aria-hidden />
            <h3 className="mt-4 text-base font-semibold text-foreground">No devices monitored yet</h3>
            <p className="mt-2 max-w-md text-sm text-muted">
              Add your first device to start monitoring network activity.
            </p>
            <Link
              to="/download"
              className="mt-6 rounded-lg px-6 py-2.5 text-sm font-semibold text-cta-text transition hover:opacity-90"
              style={{ backgroundColor: 'var(--accent-green)' }}
            >
              Download Agent
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface/80">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-muted">
                  <th className="px-4 py-3 font-medium">Device Name</th>
                  <th className="px-4 py-3 font-medium">OS</th>
                  <th className="px-4 py-3 font-medium">Monitoring</th>
                  <th className="px-4 py-3 font-medium">Last Seen</th>
                  <th className="px-4 py-3 font-medium">Risk Score</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => {
                  const rb = riskBadge(a.risk_score ?? 0);
                  const sb = statusBadge(a.status);
                  const last = a.last_seen ?? '';
                  const stale = last && msSince(last) > 24 * 60 * 60 * 1000;
                  return (
                    <tr key={a.id} className="border-b border-border/80 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{a.asset_name ?? a.device_id ?? '—'}</td>
                      <td className="px-4 py-3 text-muted">{a.os_type ?? '—'}</td>
                      <td className="px-4 py-3 text-muted">{displayDataSource(a.data_source)}</td>
                      <td
                        className="px-4 py-3"
                        style={stale ? { color: 'var(--accent-amber)' } : { color: 'var(--text-secondary)' }}
                      >
                        {last ? formatRelativeTime(last) : '—'}
                        {stale ? <span className="ml-1 text-xs">⚠</span> : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold" style={rb.style}>
                          {rb.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${sb.className}`}>
                          {sb.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/network?asset=${encodeURIComponent(a.id)}`}
                          className="text-sm font-medium text-accent hover:underline"
                        >
                          View Events
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {assetFilter ? (
          <p className="mt-3 text-sm text-muted">
            Filtering events by device.{' '}
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => {
                setSearchParams({});
              }}
            >
              Clear filter
            </button>
          </p>
        ) : null}
        <div className="mt-4 rounded-xl border border-border bg-surface/50 p-4 sm:p-5">
          <p className="text-sm text-muted">
            Download and install the ShieldSME agent for your device:
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/download"
              className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition hover:border-accent/50 hover:bg-accent/5"
            >
              Windows
            </Link>
            <Link
              to="/download"
              className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition hover:border-accent/50 hover:bg-accent/5"
            >
              macOS
            </Link>
            <Link
              to="/download"
              className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition hover:border-accent/50 hover:bg-accent/5"
            >
              Linux
            </Link>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-foreground">Recent Threat Events</h2>
          <label className="flex items-center gap-2 text-sm text-muted">
            <span className="sr-only">Filter by severity</span>
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value as SeverityFilter);
                setVisibleEventCount(20);
              }}
              className="rounded-lg border border-border bg-navy px-3 py-2 text-sm text-foreground"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
        </div>

        {loading ? (
          <SectionSkeleton rows={4} />
        ) : displayedEvents.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-border bg-surface/80 px-6 py-12 text-center">
            <span className="inline-flex text-[var(--accent-green)]" aria-hidden>
              <IconShieldCheck className="h-14 w-14" />
            </span>
            <h3 className="mt-4 text-base font-semibold text-foreground">No threats detected</h3>
            <p className="mt-2 max-w-md text-sm text-muted">
              Your monitored devices are clean. We&apos;ll alert you immediately if anything suspicious is detected.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedEvents.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                deviceName={assetNameById.get(ev.asset_id) ?? 'Device'}
                onResolve={async () => {
                  const r = await resolveNetworkEvent(ev.id);
                  if (!r.ok) {
                    setToast({ message: r.error, variant: 'error' });
                    return false;
                  }
                  return true;
                }}
                onRemoved={() => setEvents((prev) => prev.filter((e) => e.id !== ev.id))}
              />
            ))}
          </div>
        )}

        {showLoadMore && !loading && displayedEvents.length > 0 ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => void loadMoreEvents()}
              className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5"
            >
              Load more
            </button>
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-border bg-surface/60">
        <button
          type="button"
          onClick={() => setRulesOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-foreground"
          aria-expanded={rulesOpen}
        >
          Active Detection Rules
          <IconChevron open={rulesOpen} className="h-5 w-5 text-muted" />
        </button>
        {rulesOpen ? (
          <div className="border-t border-border px-4 pb-4 pt-2">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-muted">
                    <th className="py-2 pr-4 font-medium">Rule</th>
                    <th className="py-2 pr-4 font-medium">Description</th>
                    <th className="py-2 font-medium">Risk Points</th>
                  </tr>
                </thead>
                <tbody className="text-foreground">
                  <tr className="border-b border-border/80">
                    <td className="py-2 pr-4 font-medium">Suspicious Port</td>
                    <td className="py-2 pr-4 text-muted">
                      Connection to ports used by miners, Tor, backdoors, botnets
                    </td>
                    <td className="py-2">+40</td>
                  </tr>
                  <tr className="border-b border-border/80">
                    <td className="py-2 pr-4 font-medium">Known Malware Process</td>
                    <td className="py-2 pr-4 text-muted">Process name matches known malicious software</td>
                    <td className="py-2">+60</td>
                  </tr>
                  <tr className="border-b border-border/80">
                    <td className="py-2 pr-4 font-medium">Suspicious Outbound</td>
                    <td className="py-2 pr-4 text-muted">Outbound connection on non-standard business port</td>
                    <td className="py-2">+20</td>
                  </tr>
                  <tr className="border-b border-border/80">
                    <td className="py-2 pr-4 font-medium">High Frequency DNS</td>
                    <td className="py-2 pr-4 text-muted">Unusual DNS query volume — potential DNS tunneling</td>
                    <td className="py-2">+30</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Privilege Escalation</td>
                    <td className="py-2 pr-4 text-muted">Attempt to gain elevated system privileges</td>
                    <td className="py-2">+50</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>

    </div>
  );
}

function EventCard({
  ev,
  deviceName,
  onResolve,
  onRemoved,
}: {
  ev: NetworkEventRow;
  deviceName: string;
  onResolve: () => Promise<boolean>;
  onRemoved: () => void;
}) {
  const [fading, setFading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const sev = ev.severity.toLowerCase();
  const borderColor = EVENT_BORDER[sev] ?? EVENT_BORDER.low;

  const metaParts: string[] = [deviceName, ev.event_type];
  if (ev.remote_ip) {
    metaParts.push(ev.remote_port != null ? `${ev.remote_ip}:${ev.remote_port}` : ev.remote_ip);
  }

  const resolve = async () => {
    setResolving(true);
    const ok = await onResolve();
    setResolving(false);
    if (!ok) return;
    setFading(true);
    window.setTimeout(() => {
      onRemoved();
    }, 280);
  };

  return (
    <div
      className={`rounded-xl border border-border bg-surface/90 p-4 shadow-sm transition-opacity duration-300 ${
        fading ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="pr-2 text-base font-semibold text-foreground">{ev.title}</h3>
        <span
          className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase"
          style={{
            color: borderColor,
            backgroundColor: `${borderColor}22`,
          }}
        >
          {ev.severity}
        </span>
      </div>
      <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {ev.description}
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted">{metaParts.join(' · ')}</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted">{formatRelativeTime(ev.created_at)}</span>
          <button
            type="button"
            disabled={resolving}
            onClick={() => void resolve()}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-50"
          >
            Resolve
          </button>
        </div>
      </div>
    </div>
  );
}
